import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Check, X, Loader2, Phone, LifeBuoy } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

export const MOODS = [
  { score: 2, key: 'veryBad' }, { score: 4, key: 'bad' }, { score: 6, key: 'ok' }, { score: 8, key: 'good' }, { score: 10, key: 'great' },
];

export const MoodPicker = ({ value, onPick, disabled, testid = 'mood' }) => {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-5 gap-1.5" role="radiogroup" aria-label={t('aria.today.howAreYou')}>
      {MOODS.map((m) => (
        <button key={m.score} type="button" role="radio" aria-checked={value === m.score} disabled={disabled} onClick={() => onPick(m.score)}
          className={`min-h-[44px] rounded-xl border text-xs px-1 transition-colors ${value === m.score ? 'bg-violet-600 text-white border-violet-600' : 'bg-background hover:bg-violet-50 dark:hover:bg-violet-950/40 border-border'}`}
          data-testid={`${testid}-${m.score}`}>
          {t(`aria.mood.${m.key}`)}
        </button>
      ))}
    </div>
  );
};

export const AriaActionCard = ({ action, onDone }) => {
  const { t } = useTranslation();
  const [state, setState] = useState('pending');
  const [mood, setMood] = useState(null);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));

  const confirm = async () => {
    setState('loading');
    try {
      const body = {};
      if (action.type === 'checkin') body.mood_score = mood;
      if (action.type === 'update_clean_date') body.clean_date = new Date(`${date}T12:00:00`).toISOString();
      await api.post(`/aria/actions/${action.id}/confirm`, body);
      setState('confirmed');
      toast.success(t('aria.action.done'));
      onDone?.(action);
    } catch (e) {
      setState('pending');
      toast.error(e.response?.data?.detail || t('aria.error'));
    }
  };

  const cancel = async () => {
    try { await api.post(`/aria/actions/${action.id}/cancel`); } catch { /* already handled */ }
    setState('cancelled');
  };

  if (state === 'confirmed' || state === 'cancelled') {
    return <p className="text-xs text-muted-foreground mt-2" data-testid="aria-action-status">{t(state === 'confirmed' ? 'aria.action.confirmed' : 'aria.action.cancelled')}</p>;
  }
  const needsMood = action.type === 'checkin';
  return (
    <div className="mt-2 rounded-xl border border-violet-300/60 dark:border-violet-700/60 bg-background p-3 space-y-3" data-testid={`aria-action-card-${action.type}`}>
      <p className="text-sm font-medium">{t(`aria.actionType.${action.type}`, { text: action.payload?.text || action.payload?.note || '' })}</p>
      {action.type === 'reminder' && action.payload?.remind_at && (
        <p className="text-xs text-muted-foreground">{new Date(action.payload.remind_at).toLocaleString()}</p>
      )}
      {needsMood && <MoodPicker value={mood} onPick={setMood} testid="aria-action-mood" />}
      {action.type === 'update_clean_date' && (
        <div className="space-y-1">
          <label htmlFor={`restart-${action.id}`} className="text-xs text-muted-foreground">{t('aria.action.restartDate')}</label>
          <Input id={`restart-${action.id}`} type="date" max={new Date().toISOString().slice(0, 10)} value={date} onChange={(e) => setDate(e.target.value)} className="min-h-[44px]" data-testid="aria-action-date" />
          <p className="text-xs text-muted-foreground">{t('aria.action.medalsKept')}</p>
        </div>
      )}
      <div className="flex gap-2">
        <Button size="sm" onClick={confirm} disabled={state === 'loading' || (needsMood && mood === null)} className="min-h-[40px] gap-1 bg-violet-600 hover:bg-violet-700 text-white" data-testid="aria-action-confirm">
          {state === 'loading' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}{t('aria.action.confirm')}
        </Button>
        <Button size="sm" variant="ghost" onClick={cancel} className="min-h-[40px] gap-1" data-testid="aria-action-cancel"><X className="h-4 w-4" />{t('aria.action.cancel')}</Button>
      </div>
    </div>
  );
};

export const AriaCrisisCard = ({ contacts = [] }) => {
  const { t } = useTranslation();
  return (
    <div role="alert" className="mt-2 rounded-xl border-2 border-red-500/60 bg-red-50 dark:bg-red-950/40 p-3 space-y-2" data-testid="aria-crisis-card">
      <p className="text-sm font-semibold text-red-700 dark:text-red-300 flex items-center gap-2"><LifeBuoy className="h-4 w-4" />{t('aria.crisis.title')}</p>
      <div className="grid gap-2">
        {contacts.map((c) => (
          <a key={`${c.name}-${c.number}`} href={`tel:${c.number.replace(/\s/g, '')}`} className="flex items-center justify-between gap-2 rounded-lg bg-white dark:bg-red-900/40 px-3 min-h-[48px] text-red-800 dark:text-red-100" data-testid={`aria-crisis-contact-${c.number.replace(/\s/g, '')}`}>
            <span className="text-sm font-medium">{c.name}{c.note ? ` · ${t(`aria.crisis.note.${c.note}`)}` : ''}</span>
            <span className="flex items-center gap-1 font-bold"><Phone className="h-4 w-4" />{c.number}</span>
          </a>
        ))}
      </div>
      <p className="text-xs text-red-700/80 dark:text-red-200/80">{t('aria.crisis.trusted')}</p>
    </div>
  );
};
